import { Container, Section } from '@components';
import usePageMeta from '@/utils/usePageMeta';

const LAST_UPDATED = 'March 1, 2026';

const SectionHeading = ({ children }) => (
  <h2 className="text-h4 font-bold text-primary-navy mt-10 mb-3">{children}</h2>
);

const Paragraph = ({ children }) => (
  <p className="text-neutral-charcoal leading-relaxed mb-4">{children}</p>
);

const BulletList = ({ items }) => (
  <ul className="list-disc list-inside space-y-2 mb-4 text-neutral-charcoal">
    {items.map((item, i) => (
      <li key={i} className="leading-relaxed">{item}</li>
    ))}
  </ul>
);

const PrivacyPolicy = () => {
  usePageMeta({
    title: 'Privacy Policy | DataLogicHub',
    description:
      'How DataLogicHub collects, uses, and protects your personal information.',
  });

  return (
    <>
      {/* Hero */}
      <Section className="bg-primary-navy text-white py-16">
        <Container>
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-h2 font-bold mb-4">Privacy Policy</h1>
            <p className="text-white/80 text-lg">
              Your privacy matters to us. This policy explains how DataLogicHub
              collects, uses, and protects your personal information.
            </p>
            <p className="mt-4 text-sm text-primary-teal font-medium">
              Last updated: {LAST_UPDATED}
            </p>
          </div>
        </Container>
      </Section>

      {/* Content */}
      <Section className="py-12 bg-white">
        <Container>
          <div className="max-w-3xl mx-auto">

            <SectionHeading>1. Information We Collect</SectionHeading>
            <Paragraph>
              We collect information you provide directly to us and information
              generated automatically when you use our services.
            </Paragraph>
            <BulletList items={[
              'Account information: name, email address, and password when you register.',
              'Profile data: display name, profile photo, and preferences you set.',
              'Usage data: pages visited, features used, session duration, and interactions with the platform.',
              'Content data: posts, projects, and other content you create or upload.',
              'Device and log data: IP address, browser type, operating system, and referring URLs.',
              'Payment information: processed securely via third-party payment providers (we do not store card details).',
            ]} />

            <SectionHeading>2. How We Use Your Information</SectionHeading>
            <Paragraph>
              We use the information we collect to operate, maintain, and improve
              our services, and to communicate with you.
            </Paragraph>
            <BulletList items={[
              'Provide, personalise, and improve the DataLogicHub platform.',
              'Process transactions and send related billing information.',
              'Send service notifications, security alerts, and support messages.',
              'Respond to your comments, questions, and requests.',
              'Monitor and analyse usage trends to enhance user experience.',
              'Detect, investigate, and prevent fraudulent or unauthorised activity.',
              'Comply with legal obligations.',
            ]} />

            <SectionHeading>3. Sharing of Information</SectionHeading>
            <Paragraph>
              We do not sell, trade, or rent your personal information to third
              parties. We may share your information in the following limited
              circumstances:
            </Paragraph>
            <BulletList items={[
              'Service providers: trusted vendors who help us operate our platform (e.g. cloud hosting, analytics, email delivery) under strict confidentiality agreements.',
              'Business transfers: in the event of a merger, acquisition, or sale of all or part of our assets.',
              'Legal requirements: when required by law, court order, or to protect the rights and safety of DataLogicHub or others.',
              'With your consent: for any other purpose with your explicit permission.',
            ]} />

            <SectionHeading>4. Cookies and Tracking Technologies</SectionHeading>
            <Paragraph>
              We use cookies and similar tracking technologies to collect usage
              data, maintain your session, and remember your preferences. You can
              control cookies through your browser settings, though disabling them
              may affect some functionality.
            </Paragraph>
            <BulletList items={[
              'Essential cookies: required for the platform to function correctly.',
              'Analytics cookies: help us understand how you interact with our services.',
              'Preference cookies: remember your settings and personalisation choices.',
            ]} />

            <SectionHeading>5. Data Retention</SectionHeading>
            <Paragraph>
              We retain your personal information for as long as your account is
              active or as needed to provide services. You may request deletion of
              your account and associated data at any time by contacting us. Some
              information may be retained for legal or fraud-prevention purposes
              even after account deletion.
            </Paragraph>

            <SectionHeading>6. Security</SectionHeading>
            <Paragraph>
              We implement industry-standard technical and organisational measures
              to protect your information against unauthorised access, alteration,
              disclosure, or destruction. These include encrypted data transmission
              (TLS), secure cloud infrastructure (Firebase / Google Cloud), and
              access controls. However, no internet transmission is completely
              secure, and we cannot guarantee absolute security.
            </Paragraph>

            <SectionHeading>7. Your Rights</SectionHeading>
            <Paragraph>
              Depending on your location, you may have the following rights
              regarding your personal data:
            </Paragraph>
            <BulletList items={[
              'Access: request a copy of the personal data we hold about you.',
              'Correction: request correction of inaccurate or incomplete data.',
              'Deletion: request erasure of your personal data.',
              'Portability: receive your data in a structured, machine-readable format.',
              'Objection: object to certain processing activities.',
              'Withdraw consent: where processing is based on your consent, withdraw it at any time.',
            ]} />
            <Paragraph>
              To exercise any of these rights, please contact us at{' '}
              <a
                href="mailto:privacy@datalogichub.com"
                className="text-primary-teal hover:underline font-medium"
              >
                privacy@datalogichub.com
              </a>.
            </Paragraph>

            <SectionHeading>8. Third-Party Links</SectionHeading>
            <Paragraph>
              Our platform may contain links to third-party websites or services.
              We are not responsible for the privacy practices of those sites.
              We encourage you to review the privacy policy of every website you
              visit.
            </Paragraph>

            <SectionHeading>9. Children's Privacy</SectionHeading>
            <Paragraph>
              DataLogicHub is not directed to children under 13 years of age. We
              do not knowingly collect personal information from children. If you
              believe a child has provided us with personal information, please
              contact us and we will delete it promptly.
            </Paragraph>

            <SectionHeading>10. Changes to This Policy</SectionHeading>
            <Paragraph>
              We may update this Privacy Policy from time to time. We will notify
              you of significant changes by posting the new policy on this page
              and updating the "Last updated" date. Your continued use of
              DataLogicHub after any changes constitutes your acceptance of the
              updated policy.
            </Paragraph>

            <SectionHeading>11. Contact Us</SectionHeading>
            <Paragraph>
              If you have any questions, concerns, or requests regarding this
              Privacy Policy, please reach out to us:
            </Paragraph>
            <div className="bg-neutral-soft-gray rounded-xl p-6 text-neutral-charcoal space-y-1">
              <p className="font-semibold text-primary-navy">DataLogicHub</p>
              <p>
                Email:{' '}
                <a
                  href="mailto:privacy@datalogichub.com"
                  className="text-primary-teal hover:underline"
                >
                  privacy@datalogichub.com
                </a>
              </p>
              <p>Website: <span className="text-primary-teal">datalogichub.com</span></p>
            </div>

          </div>
        </Container>
      </Section>
    </>
  );
};

export default PrivacyPolicy;
